'use client'
import { useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import s from "./LazyVideo.module.scss";
import { createVideoSchema } from "@/lib/constants";

interface LazyVideoProps {
    alt?: string;
    src: string;
    defaultInView?: boolean;
    autoplay?: boolean;
    controls?: boolean;
}

export default function LazyVideo({
    alt = "",
    src = "",
    defaultInView = false,
    autoplay,
    controls,
}: LazyVideoProps) {
    const [hasBeenInView, setHasBeenInView] = useState(false);
    const { ref, inView } = useInView({
        threshold: 0.1,
        triggerOnce: false,
        initialInView: defaultInView,
    });

    const videoRef = useRef<HTMLVideoElement>(null);

    // play() devuelve una promesa que se rechaza si el navegador bloquea el autoplay;
    // el catch evita un unhandled rejection
    const tryPlay = () => {
        videoRef.current?.play().catch(() => { });
    };

    useEffect(() => {
        // Si el video está en el área visible y no ha sido reproducido aún, y el autoplay está activado
        if (inView && !hasBeenInView && autoplay) {
            setHasBeenInView(true);
            tryPlay();
        } else if (inView && !hasBeenInView && !autoplay) {
            setHasBeenInView(true)
        } else if (!inView) {
            videoRef.current?.pause();
        } else if (inView && autoplay) {
            tryPlay();
        }
    }, [inView, autoplay, hasBeenInView]);

    const handleVideoClick = () => {
        if (videoRef.current) {
            if (videoRef.current.paused) {
                tryPlay();
            } else {
                videoRef.current.pause();
            }
        }
    };

    const videoSchema = createVideoSchema({ src, alt });

    return (
        <div className={s.lazyVideo}>
            <div
                ref={ref}
                style={{ width: "100%" }}
                className={s.contentVideo}
                onClick={handleVideoClick}
            >
                {controls ? (
                    <video
                        ref={videoRef}
                        src={hasBeenInView ? src : undefined}
                        autoPlay={autoplay}
                        muted
                        controls
                        loop
                        playsInline
                    />
                ) : (
                    <video
                        ref={videoRef}
                        src={hasBeenInView ? src : undefined}
                        autoPlay={autoplay}
                        muted
                        loop
                        playsInline
                    />
                )}
            </div>
            <script type="application/ld+json" async dangerouslySetInnerHTML={{ __html: JSON.stringify(videoSchema) }} />
        </div>
    );
}